/**
 * Shared Layout Shell for all Cloud GM Company Agent pages
 * (Feb 2026 / iter-181). Matches the ControlCloudGM design pattern:
 *   • bg-[#030303] root
 *   • fixed glow background layer
 *   • DashboardHeader with onBack
 *   • Breadcrumb inside a border-b container
 *   • main wrapper with max-w-7xl and padding
 */
import React from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';

export const AgentPageShell = ({ breadcrumb = [], onBack, maxWidth = 'max-w-7xl', children }) => (
  <div className="min-h-screen bg-[#030303]">
    {/* Background glow */}
    <div className="fixed inset-0 pointer-events-none">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#10b981]/5 rounded-full blur-[150px]" />
    </div>

    <DashboardHeader onBack={onBack} />

    {breadcrumb.length > 0 && (
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>
    )}

    <main className="relative z-10 py-6">
      <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
        {children}
      </div>
    </main>
  </div>
);

// Utility — days remaining until an ISO date. Returns null when input is empty
// or unparseable. Negative values mean the date is already in the past.
export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  // Compare at day-boundary to avoid off-by-one on HH:MM differences.
  const t = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const n = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((t - n) / 86_400_000);
};

// Small pill that renders "X يوم متبقي" / "X days left" or "منتهي منذ X" / "expired X days ago"
// with a colour based on urgency. Returns null if `dateStr` is empty.
export const ExpiryBadge = ({ dateStr, language = 'ar', label }) => {
  const days = daysUntil(dateStr);
  if (days === null) return null;

  const isAr = language === 'ar';
  let color = 'emerald', text;
  if (days < 0) {
    color = 'red';
    text = isAr ? `منتهي منذ ${Math.abs(days)} يوم` : `Expired ${Math.abs(days)}d ago`;
  } else if (days === 0) {
    color = 'red';
    text = isAr ? 'ينتهي اليوم' : 'Expires today';
  } else if (days <= 7) {
    color = 'red';
    text = isAr ? `متبقي ${days} أيام` : `${days}d left`;
  } else if (days <= 30) {
    color = 'amber';
    text = isAr ? `متبقي ${days} يوم` : `${days}d left`;
  } else {
    color = 'emerald';
    text = isAr ? `متبقي ${days} يوم` : `${days}d left`;
  }

  const palette = {
    red:     { bg: 'bg-red-500/10',     border: 'border-red-500/30',     txt: 'text-red-300'     },
    amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   txt: 'text-amber-300'   },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', txt: 'text-emerald-300' },
  }[color];

  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${palette.bg} ${palette.border} ${palette.txt}`}>
      {label && <span className="opacity-70">{label}:</span>} {text}
    </span>
  );
};

export default AgentPageShell;
